import { apiGet, apiPost } from '../../../adapters/api-client/api';
import type { SessionState } from './cycle-progress';
import type { EffectiveLearningProfile, LearningGateFact } from './learning';

/**
 * Session evidence (ADR-007 Epic 8, TCH-14).
 *
 * These endpoints shipped without a caller, which left the teacher no way to
 * record the evidence that ADR-005 makes mandatory before escrow releases.
 */

/**
 * Why a milestone is not yet releasable — mirrors the backend
 * EvidenceFailureReason. Delivery facts only: since MC-01 v1-7 no rubric,
 * final rubric or deliverable is a payment condition, in any profile.
 */
export type EvidenceFailureReason =
  | 'duration_below_minimum'
  | 'recording_not_started'
  | 'recording_not_ready'
  | 'teacher_check_in_missing'
  | 'learner_no_show'
  | 'attendance_pending'
  | 'attendance_unknown'
  | 'session_interrupted'
  | 'sessions_not_delivered';

/** Provider-derived attendance for the learner at this session (UC-S09). */
export type LearnerAttendance = 'PRESENT' | 'LATE' | 'NO_SHOW' | 'UNKNOWN';

/** Recording lifecycle as the provider reports it. Never a location. */
export type RecordingStatus = 'pending' | 'recording' | 'processing' | 'ready' | 'failed';

export interface RubricScore {
  criterion: string;
  score: number;
  comment?: string;
}

export interface Rubric {
  scores: RubricScore[];
  overall: number;
  summary: string;
  submittedAt?: string;
  /** T10-05: 1 for the first submission; each correction is the next version. */
  version?: number;
  priorVersion?: number;
  correctionReason?: string;
}

export interface MilestoneEvidence {
  milestoneId: string;
  enrollmentId: string;
  sequence: number;
  type: 'session' | 'final_project';
  status: string;
  amount: number;
  bookingId?: string;
  /**
   * The API projects evidence per reader: no recording location, no provider
   * ids, and the teacher's notes only for the teacher of record.
   */
  recordingStatus?: RecordingStatus;
  scheduledStart?: string;
  sessionStartedAt?: string;
  sessionEndedAt?: string;
  durationMinutes?: number;
  teacherCheckedInAt?: string;
  /** First learner join, as the video provider reported it. */
  studentCheckedInAt?: string;
  attendance?: LearnerAttendance;
  /** DEC-S3-T09-08: the meeting ended with no End Class. */
  sessionOutcome?: 'INTERRUPTED';
  learnerPresenceSeconds?: number;
  learnerFirstJoinedAt?: string;
  learnerLastLeftAt?: string;
  rubric?: Rubric;
  finalRubric?: Rubric;
  deliverableUri?: string;
  teacherNotes?: string;
  /** TCH-14-FR-10 — the version of `teacherNotes`; each save is a new version. */
  teacherNotesVersion?: number;
  evidenceFailureReason?: string;
}

/** What the evidence endpoint returns: the record plus what is still missing. */
export interface EvidenceView {
  milestone: MilestoneEvidence;
  validation: { valid: boolean; reasons: EvidenceFailureReason[] };
  sessionState: SessionState;
  /** What the milestone's escrow amount is denominated in. */
  currency: string;
  /** LEM-01. Absent from an older API, which means LEP_2. */
  learningProfile?: EffectiveLearningProfile;
  /** T10-04: learning and delivery evidence, reported beside — not as — release. */
  gateFact?: LearningGateFact;
}

/** The shape every write endpoint returns once it has re-evaluated the evidence. */
export interface EvidenceWriteResult {
  milestone: MilestoneEvidence;
  validation: { valid: boolean; reasons: EvidenceFailureReason[] };
  completed: boolean;
}

const base = (enrollmentId: string, sequence: number) =>
  `/api/v1/enrollments/${enrollmentId}/sessions/${sequence}`;

/** A participant's read: the teacher of record or the family. Takes no reason. */
export function getEvidence(enrollmentId: string, sequence: number): Promise<EvidenceView> {
  return apiGet(`${base(enrollmentId, sequence)}/evidence`);
}

/**
 * DEC-A01-06 — staff opening a session's raw evidence state why. An operator
 * may write that reason about a child, so it travels in the body, never in a URL.
 */
export function retrieveEvidence(
  enrollmentId: string,
  sequence: number,
  reason: string,
): Promise<EvidenceView> {
  return apiPost(`${base(enrollmentId, sequence)}/evidence/retrieve`, { reason });
}

export function endSessionMilestone(
  enrollmentId: string,
  sequence: number,
): Promise<EvidenceWriteResult> {
  return apiPost(`${base(enrollmentId, sequence)}/end`, {});
}

export function submitRubric(
  enrollmentId: string,
  sequence: number,
  rubric: { scores: RubricScore[]; overall: number; summary: string; asFinalRubric?: boolean },
): Promise<EvidenceWriteResult> {
  return apiPost(`${base(enrollmentId, sequence)}/rubric`, rubric);
}

export function submitDeliverable(
  enrollmentId: string,
  sequence: number,
  deliverableUri: string,
): Promise<EvidenceWriteResult> {
  return apiPost(`${base(enrollmentId, sequence)}/deliverable`, { deliverableUri });
}

/**
 * TCH-14-FR-10 — save the teacher's private notes. The first save omits
 * `expectedVersion`; changing saved notes names the version being changed and
 * says why, and the server keeps the earlier version.
 */
export function submitNotes(
  enrollmentId: string,
  sequence: number,
  body: { notes: string; expectedVersion?: number; reason?: string; requestKey: string },
): Promise<MilestoneEvidence> {
  return apiPost(`${base(enrollmentId, sequence)}/notes`, body);
}

/** Attendance, in the app's language. Late is attendance; only never joining is absence. */
export const ATTENDANCE_LABELS: Record<LearnerAttendance, string> = {
  PRESENT: 'Presente',
  LATE: 'Llegó tarde',
  NO_SHOW: 'No asistió',
  // DEC-S3-S09-05: the provider's facts are missing. Neither present nor absent.
  UNKNOWN: 'Sin confirmar',
};

export const RECORDING_STATUS_LABELS: Record<RecordingStatus, string> = {
  pending: 'Pendiente',
  recording: 'Grabando',
  processing: 'Procesando',
  ready: 'Lista',
  failed: 'Falló',
};

/** `2400` → `40 min`; under a minute reads as `menos de 1 min`. */
export const formatPresence = (seconds?: number): string => {
  if (typeof seconds !== 'number') return '—';
  const minutes = Math.floor(seconds / 60);
  return minutes < 1 ? 'menos de 1 min' : `${minutes} min`;
};

/**
 * The rubric criteria IQMax grades every session against. Kept here rather than
 * free-form so scores stay comparable across teachers and courses; the teacher
 * may still add a per-criterion comment.
 */
export const SESSION_RUBRIC_CRITERIA = [
  { key: 'participacion', label: 'Participación' },
  { key: 'comprension', label: 'Comprensión' },
  { key: 'progreso', label: 'Progreso frente a la sesión anterior' },
] as const;

export const FINAL_PROJECT_RUBRIC_CRITERIA = [
  { key: 'dominio', label: 'Dominio del tema' },
  { key: 'aplicacion', label: 'Aplicación práctica' },
  { key: 'presentacion', label: 'Presentación' },
] as const;

/** Human-readable, in the app's language, for each blocking reason. */
export const EVIDENCE_REASON_LABELS: Record<EvidenceFailureReason, string> = {
  recording_not_started: 'La sesión nunca se inició, así que no hay grabación.',
  recording_not_ready: 'La grabación todavía se está procesando.',
  teacher_check_in_missing: 'La videollamada no ha confirmado la entrada del profesor.',
  // The minimum is a governed number; the screen does not repeat it.
  duration_below_minimum: 'La sesión no alcanzó la duración mínima.',
  learner_no_show: 'El alumno no entró a la clase.',
  attendance_pending: 'La asistencia se registra cuando termina la clase.',
  attendance_unknown: 'La videollamada no ha confirmado la asistencia de esta sesión.',
  session_interrupted: 'La sesión se interrumpió y falta clasificarla.',
  sessions_not_delivered: 'Todavía no se han impartido todas las sesiones del ciclo.',
};
